<template>
    <div class="page">
        <h1>Xeenia M2M demó</h1>
        <p class="lead">
            Fejlesztői segédeszköz a Xeenia gépi integrációhoz. A kérések bearer tokennel és
            HMAC-SHA256 aláírással mennek ki, közvetlenül a böngészőből. A mezők a helyi demo
            backendhez vannak előre kitöltve, így elég a gombot megnyomni.
        </p>

        <section>
            <h2>Teljes kör</h2>
            <div class="row">
                <button class="primary" :disabled="busy" @click="runAll">
                    {{ busy ? 'Fut…' : 'Teljes kör futtatása' }}
                </button>
                <span class="desc">
                    Katalógus → referencialista → létrehozás → visszaolvasás → módosítás → életciklus
                </span>
            </div>

            <p v-if="preflightError" class="error">{{ preflightError }}</p>

            <ol v-if="steps.length" class="steps">
                <li v-for="step in steps" :key="step.key" :class="step.state">
                    <div class="step-head">
                        <span class="badge">{{ badge(step.state) }}</span>
                        <strong>{{ step.label }}</strong>
                        <span v-if="step.status" class="mono">HTTP {{ step.status }}</span>
                        <span v-if="step.request" class="mono dim">{{ step.request }}</span>
                    </div>
                    <p v-if="step.note" class="desc">{{ step.note }}</p>
                    <details v-if="step.body">
                        <summary>Válasz törzse</summary>
                        <pre>{{ step.body }}</pre>
                    </details>
                </li>
            </ol>

            <p v-if="summary" class="desc">{{ summary }}</p>
        </section>

        <section>
            <h2>
                <button class="link" @click="settingsOpen = !settingsOpen">
                    Beállítások {{ settingsOpen ? '▾' : '▸' }}
                </button>
            </h2>
            <div v-if="settingsOpen">
                <div class="grid">
                    <label>Alap URL
                        <input v-model="baseUrl" type="text">
                    </label>
                    <label>Gépi token
                        <input v-model="token" type="text">
                    </label>
                    <label>Aláíró kulcs
                        <input v-model="secret" type="text">
                    </label>
                    <label>Külső referencia
                        <input v-model="externalRef" type="text">
                    </label>
                    <label>Páciens azonosító
                        <input v-model="patientId" type="text">
                    </label>
                    <label>Komponens verzió azonosító
                        <input v-model="componentVersionId" type="text">
                    </label>
                    <label>Referencialista kulcs
                        <input v-model="listKey" type="text">
                    </label>
                    <label>Életciklus művelet
                        <select v-model="lifecycleAction">
                            <option value="abort">abort</option>
                            <option value="pause">pause</option>
                        </select>
                    </label>
                </div>
                <div class="row">
                    <button @click="resetSettings">Alapértelmezések visszaállítása</button>
                </div>
                <p class="note">
                    A teljes kör a komponens verzió azonosítót a katalógus válaszából veszi,
                    ide csak a lefutás után írja vissza az egyedi hívások kedvéért.
                </p>
            </div>
        </section>

        <section>
            <h2>Olvasó végpontok</h2>
            <div v-for="ep in readEndpoints" :key="ep.key" class="row">
                <button :disabled="busy" @click="callRead(ep)">
                    {{ ep.method }} {{ resolvePath(ep.path) }}
                </button>
                <span class="desc">{{ ep.label }}: {{ ep.description }}</span>
            </div>
        </section>

        <section>
            <h2>Író végpontok</h2>
            <label class="block">Példa törzs
                <select v-model="selectedKey" @change="loadExample">
                    <option v-for="ex in examples" :key="ex.key" :value="ex.key">{{ ex.label }}</option>
                </select>
            </label>
            <p class="note">
                {{ selected.method }} {{ resolvePath(writePaths[selected.pathKey]) }}
            </p>
            <textarea v-model="editorBody" spellcheck="false" rows="22"></textarea>
            <div class="row">
                <button :disabled="busy" @click="sendWrite">Küldés</button>
                <button :disabled="busy" @click="loadExample">Példa visszatöltése</button>
            </div>
        </section>

        <section>
            <h2>Egyedi hívás válasza</h2>
            <p v-if="!result" class="note">Még nem futott egyedi hívás.</p>
            <template v-else>
                <p class="status">
                    <strong>{{ result.request }}</strong><br>
                    HTTP {{ result.status }} {{ result.statusText }}
                </p>
                <pre>{{ result.body }}</pre>
            </template>
        </section>
    </div>
</template>

<script>
import { signedRequest } from './signing';
import { readEndpoints, writePaths } from './endpoints';
import { examples } from './payloads';
import { defaults, BACKEND_DOWN_HINT } from './config';
import { stepDefinitions, preflight, runRoundTrip } from './flow';

export default {
    name: 'App',

    data() {
        return {
            baseUrl: defaults.baseUrl,
            token: defaults.token,
            secret: defaults.secret,
            patientId: defaults.patientId,
            externalRef: `XEENIA-DEMO-${Date.now()}`,
            componentVersionId: '',
            listKey: 'vitamins',
            lifecycleAction: 'abort',
            settingsOpen: false,
            readEndpoints,
            writePaths,
            examples,
            selectedKey: examples[0].key,
            editorBody: '',
            steps: [],
            summary: '',
            preflightError: '',
            result: null,
            busy: false,
        };
    },

    computed: {
        selected() {
            return this.examples.find((e) => e.key === this.selectedKey) || this.examples[0];
        },

        context() {
            return {
                externalRef: this.externalRef,
                patientId: this.patientId,
                componentVersionId: this.componentVersionId || '00000000-0000-0000-0000-000000000000',
            };
        },

        connection() {
            return {
                baseUrl: this.baseUrl,
                token: this.token,
                secret: this.secret,
                patientId: this.patientId,
                externalRef: this.externalRef,
                lifecycleAction: this.lifecycleAction,
            };
        },
    },

    created() {
        this.loadExample();
    },

    methods: {
        badge(state) {
            return { ok: '✓', fail: '✗', running: '…', skip: '–' }[state] || '·';
        },

        resetSettings() {
            this.baseUrl = defaults.baseUrl;
            this.token = defaults.token;
            this.secret = defaults.secret;
            this.patientId = defaults.patientId;
            this.lifecycleAction = 'abort';
        },

        async runAll() {
            this.busy = true;
            this.preflightError = '';
            this.summary = '';
            // Every run gets a fresh reference so it does not land on the idempotent branch.
            this.externalRef = `XEENIA-DEMO-${Date.now()}`;
            this.steps = stepDefinitions.map((s) => ({ ...s, state: 'pending', status: null, body: '', note: '', request: '' }));

            try {
                const check = await preflight(this.connection);

                if (!check.ok) {
                    this.preflightError = check.message || BACKEND_DOWN_HINT.replace('{base}', this.baseUrl);
                    this.steps = [];

                    return;
                }

                const outcome = await runRoundTrip(this.connection, this.report, check.response);

                this.summary = outcome.finished
                    ? `A teljes kör lefutott. Külső referencia: ${outcome.externalRef}`
                    : 'A kör megszakadt, a piros lépés válasza mutatja az okot.';
            } catch (e) {
                this.preflightError = `${BACKEND_DOWN_HINT.replace('{base}', this.baseUrl)} (${e})`;
            } finally {
                this.busy = false;
            }
        },

        report(key, patch) {
            const step = this.steps.find((s) => s.key === key);

            if (!step) {
                return;
            }

            Object.assign(step, {
                state: patch.state,
                status: patch.status ?? step.status,
                request: patch.request ?? step.request,
                note: patch.note ?? step.note,
                body: patch.body ?? step.body,
            });

            if (key === 'catalog' && patch.state === 'ok' && patch.json) {
                const component = (patch.json.components || []).find((c) => c.component_version_id);
                this.componentVersionId = component?.component_version_id || this.componentVersionId;
                this.listKey = patch.json.lists?.[0]?.key || this.listKey;
                this.loadExample();
            }
        },

        resolvePath(path) {
            return path
                .replace('{externalRef}', encodeURIComponent(this.externalRef))
                .replace('{listKey}', encodeURIComponent(this.listKey));
        },

        loadExample() {
            this.editorBody = JSON.stringify(this.selected.build(this.context), null, 2);
        },

        async callRead(endpoint) {
            await this.run(endpoint.method, this.resolvePath(endpoint.path), '');
        },

        async sendWrite() {
            let raw = this.editorBody;
            try {
                // Serialised once: exactly these bytes are signed and sent.
                raw = JSON.stringify(JSON.parse(this.editorBody));
            } catch (e) {
                this.result = {
                    request: 'ellenőrzés',
                    status: '-',
                    statusText: 'érvénytelen JSON',
                    body: String(e),
                };

                return;
            }

            await this.run(this.selected.method, this.resolvePath(this.writePaths[this.selected.pathKey]), raw);
        },

        async run(method, path, rawBody) {
            this.busy = true;
            try {
                const res = await signedRequest({
                    baseUrl: this.baseUrl,
                    token: this.token,
                    secret: this.secret,
                    method,
                    path,
                    rawBody,
                });
                this.result = { request: `${method} ${path}`, ...res };
            } catch (e) {
                this.result = {
                    request: `${method} ${path}`,
                    status: '-',
                    statusText: 'a kérés nem ért célba',
                    body: BACKEND_DOWN_HINT.replace('{base}', this.baseUrl),
                };
            } finally {
                this.busy = false;
            }
        },
    },
};
</script>

<style>
body {
    margin: 0;
    background: #fff;
    color: #222;
    font-family: system-ui, sans-serif;
    font-size: 14px;
}

.page {
    max-width: 900px;
    margin: 0 auto;
    padding: 16px;
}

h1 {
    font-size: 20px;
}

h2 {
    font-size: 16px;
    margin-bottom: 8px;
}

section {
    border-top: 1px solid #ddd;
    padding-top: 12px;
    margin-top: 20px;
}

.grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
}

label {
    display: block;
    font-size: 12px;
}

label.block {
    margin-bottom: 8px;
}

input,
select,
textarea {
    width: 100%;
    box-sizing: border-box;
    padding: 5px;
    font-family: monospace;
    font-size: 13px;
    border: 1px solid #bbb;
}

textarea {
    white-space: pre;
}

button {
    padding: 6px 10px;
    margin: 4px 8px 4px 0;
    font-family: monospace;
    cursor: pointer;
}

button.primary {
    font-size: 15px;
    padding: 10px 18px;
    background: #1a7f37;
    color: #fff;
    border: 0;
}

button.primary:disabled {
    background: #8a8a8a;
}

button.link {
    background: none;
    border: 0;
    padding: 0;
    margin: 0;
    font: inherit;
    color: #1a4f9c;
    cursor: pointer;
}

.row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.desc,
.note,
.lead {
    color: #555;
    font-size: 12px;
}

.error {
    background: #fdecec;
    border: 1px solid #d33;
    color: #a11;
    padding: 10px;
    font-size: 13px;
}

.steps {
    list-style: none;
    padding: 0;
    margin: 12px 0 0;
}

.steps li {
    border-left: 4px solid #ccc;
    padding: 6px 10px;
    margin-bottom: 8px;
    background: #fafafa;
}

.steps li.ok {
    border-left-color: #1a7f37;
}

.steps li.fail {
    border-left-color: #d33;
}

.steps li.running {
    border-left-color: #c88a00;
}

.steps li.skip {
    border-left-color: #888;
}

.step-head {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.badge {
    font-family: monospace;
    font-weight: bold;
}

.mono {
    font-family: monospace;
    font-size: 12px;
}

.dim {
    color: #777;
}

summary {
    font-size: 12px;
    cursor: pointer;
    color: #1a4f9c;
}

pre {
    background: #f4f4f4;
    border: 1px solid #ddd;
    padding: 10px;
    overflow: auto;
    max-height: 460px;
}

.status {
    font-family: monospace;
}
</style>
